import { Search, Download } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { DatePicker } from '../../ui/date-picker';
import { MultiCombobox } from '../../ui/multi-combobox';
import { exportCsv } from '../../dashboard';
import {
    AnalyticsOptions,
    DetailedRow,
} from '../../../services/api/CPTAnalytics/CPTActivityService';
import { Label } from '../../ui/label';

interface CPTFiltersSectionProps {
    start: string;
    end: string;
    selectedTechs: string[];
    selectedCptCodes: string[];
    options: AnalyticsOptions;
    loading: boolean;
    canSearch: boolean;
    detailedRows: DetailedRow[];
    onStartChange: (value: string) => void;
    onEndChange: (value: string) => void;
    onTechsChange: (value: string[]) => void;
    onCptCodesChange: (value: string[]) => void;
    onRunAnalytics: () => void;
}

export default function CPTFiltersSection({
    start,
    end,
    selectedTechs,
    selectedCptCodes,
    options,
    loading,
    canSearch,
    detailedRows,
    onStartChange,
    onEndChange,
    onTechsChange,
    onCptCodesChange,
    onRunAnalytics,
}: CPTFiltersSectionProps) {
    const handleExport = () => {
        exportCsv(
            [
                'Scan Date',
                'User',
                'Device Type',
                'CPT',
                'Quantity',
                'RVU/Unit',
                'Total RVU',
                'Estimated Revenue',
            ],
            [
                ...detailedRows.map((row) => [
                    row.date,
                    row.techName,
                    row.deviceType || '',
                    row.cptCode,
                    row.quantity,
                    row.rvuPerUnit,
                    row.totalRVU.toFixed(2),
                    `$${row.estimatedRevenue.toFixed(2)}`,
                ]),
                [
                    'Total',
                    '',
                    '',
                    '',
                    detailedRows.reduce((sum, r) => sum + r.quantity, 0),
                    '',
                    detailedRows.reduce((sum, r) => sum + r.totalRVU, 0).toFixed(2),
                    `$${detailedRows.reduce((sum, r) => sum + r.estimatedRevenue, 0).toFixed(2)}`,
                ],
            ],
            `cpt-report-${start}-to-${end}.csv`
        );
    };

    return (
        <Card className="border-subtle">
            <CardContent className="p-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Start Date */}
                    <div>
                        <Label className="mb-2 flex items-center gap-1.5">
                            Start Date
                        </Label>
                        <DatePicker
                            value={start}
                            onChange={onStartChange}
                            placeholder="Start date"
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <Label className="mb-2 flex items-center gap-1.5">
                            End Date
                        </Label>
                        <DatePicker
                            value={end}
                            onChange={onEndChange}
                            placeholder="End date"
                        />
                    </div>

                    {/* User */}
                    <div>
                        <Label className="mb-2 flex items-center gap-1.5">
                            Users (Optional)
                        </Label>
                        <MultiCombobox
                            options={options.techNames || []}
                            value={selectedTechs}
                            onChange={onTechsChange}
                            placeholder="All users"
                            searchPlaceholder="Search users..."
                            emptyText="No users found."
                        />
                    </div>

                    {/* CPT Code */}
                    <div>
                        <Label className="mb-2 flex items-center gap-1.5">
                            CPT Codes (Optional)
                        </Label>
                        <MultiCombobox
                            options={options.cptCodes || []}
                            value={selectedCptCodes}
                            onChange={onCptCodesChange}
                            placeholder="All codes"
                            searchPlaceholder="Search codes..."
                            emptyText="No codes found."
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2 border-t border-subtle pt-4">
                    <Button
                        variant="outline"
                        disabled={detailedRows.length === 0}
                        onClick={handleExport}
                    >
                        <Download className="mr-1.5 h-4 w-4" />
                        Export CSV
                    </Button>
                    <Button
                        onClick={onRunAnalytics}
                        disabled={!canSearch || loading}
                    >
                        <Search className="mr-1.5 h-4 w-4" />
                        {loading ? 'Loading...' : 'Run Analytics'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
